import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import Modal from 'react-modal';
import { UserPlus } from 'lucide-react';
import { hideAddIncidentAdminModal } from '@@/features/modal';
import { useAddPropertyIncidentAdminMutation } from '@@/services/property';
import { RootState } from '@@/store';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import StandardInput from '@@/components/Inputs/StandardInput';
import PhonePickerInput from '@@/components/Inputs/PhonePickerInput';
import CheckboxInput from '@@/components/Inputs/CheckboxInput';

const schema = yup
  .object({
    firstName: yup.string().trim(),
    lastName: yup.string().trim(),
    jobTitle: yup.string().trim(),
    email: yup
      .string()
      .email('Veuillez entrer un email valide')
      .required('Veuillez entrer un email valide'),
    phoneNumber: yup.string(),
  })
  .required();

const AddIncidentAdminModal: React.FC<{ propertyId: string }> = ({
  propertyId,
}) => {
  const dispatch = useDispatch();
  const showAddIncidentAdminModal = useSelector(
    (state: RootState) => state.modal.showAddIncidentAdminModal,
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Omit<Alacaza.PropertyAdmin, 'id'>>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
  });

  const closeModal = useCallback(() => {
    dispatch(hideAddIncidentAdminModal());
    reset();
  }, [dispatch, reset]);

  const [addPropertyIncidentAdmin, { isSuccess }] =
    useAddPropertyIncidentAdminMutation();

  const onSubmit = useCallback(
    (data: Omit<Alacaza.PropertyAdmin, 'id'>) => {
      if (propertyId) {
        addPropertyIncidentAdmin({
          propertyId,
          body: data,
        });
      }
    },
    [addPropertyIncidentAdmin, propertyId],
  );

  useEffect(() => {
    return closeModal();
  }, [closeModal]);

  useEffect(() => {
    if (isSuccess) {
      closeModal();
      reset();
      toast.success(`Le gestionnaire d'incidents a été ajouté`, {
        position: 'top-right',
      });
    }
  }, [isSuccess, reset, closeModal]);

  return (
    <Modal
      isOpen={showAddIncidentAdminModal}
      onRequestClose={closeModal}
      className="absolute bg-white py-6 px-8 border border-neutral-300 rounded-modal shadow-modal top-20 left-8 sm:top-1/2 sm:left-1/2 sm:transform sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-[600px] max-h-[90vh] overflow-y-auto"
      contentLabel="Show Add Incident Comment Modal"
      ariaHideApp={false}
    >
      <div className="flex items-center justify-center gap-2 mb-6">
        <UserPlus size={24} className="text-primary-600" />
        <h2 className="text-heading-md text-neutral-900">Ajouter un gestionnaire</h2>
      </div>

      <hr className="mb-6 border-neutral-200" />

      <form className="space-y-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <StandardInput
            register={register}
            id="firstName"
            label="Prénom"
            type="text"
            placeholder="Prénom"
            error={errors.firstName}
          />
          <StandardInput
            register={register}
            id="lastName"
            label="Nom"
            type="text"
            placeholder="Nom"
            error={errors.lastName}
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <StandardInput
            register={register}
            id="jobTitle"
            label="Fonction"
            type="text"
            placeholder="Fonction"
            error={errors.jobTitle}
          />
          <StandardInput
            register={register}
            id="email"
            label="Email *"
            type="text"
            placeholder="Email"
            error={errors.email}
          />
        </div>

        <PhonePickerInput control={control} id="phoneNumber" />

        <CheckboxInput
          register={register}
          id="isPrivate"
          label="Non visible par les résidents"
        />

        <CheckboxInput
          register={register}
          id="disableNotifications"
          label="Ne pas envoyer de notifications"
        />
      </form>

      <div className="flex justify-end mt-6 pt-6 border-t border-neutral-200">
        <button
          className="px-6 py-3 rounded-button bg-primary-600 hover:bg-primary-700 text-white shadow-button transition-colors duration-200 text-body-sm font-medium"
          onClick={handleSubmit(onSubmit)}
        >
          Valider
        </button>
      </div>
    </Modal>
  );
};

export default AddIncidentAdminModal;
